import { memo, type ReactNode } from 'react';

import styles from './index.module.css';

export interface ScoreBlockProps {
  /** 0-100. Absent or non-numeric means the candidate has not been scored. */
  score?: number | null;
  /** InterviewStatusBadge, while an interview is under way. Optional and
   * additive -- every existing call site renders exactly as before. */
  badge?: ReactNode;
  /** FitScoreHistoryNote, once an interview has moved this score from its
   * resume-derived value. */
  historyNote?: ReactNode;
}

/**
 * The fit score, as the page's first fact.
 *
 * NO THRESHOLD MARKER, deliberately. The reference mockup draws a "65 Fitscore
 * threshold" chip beside this number. There is no such threshold:
 * `candidates.min-fit-score` was removed in avrio-backend#38 and selection is
 * percentile per job per pool, because a fixed bar surfaces nobody on a hard
 * job — job 3855 returned fifty candidates whose best score was 56 and sourced
 * no one. Drawing a bar this score is "above" would invent a gate the product
 * does not have, and would read as pass/fail to the person looking at it.
 *
 * NO BAND COLOUR ON THE NUMBER EITHER, for a related reason. A green 80 and a
 * red 40 imply a fixed scale with fixed meaning, and the scale is mid-migration:
 * SCORING_VERSION is 1, which maps a weighted 1-5 average onto 20-100 and
 * floors at 20, while C5a remaps to a true 1-100 and moves every stored score
 * down. No record carries a version stamp, so this number cannot currently be
 * attributed to a scale at all. Colouring it would add a claim on top of one
 * that is already unsafe. The number is shown plainly until the migration
 * lands and records are stamped.
 */
export const ScoreBlock = memo(({ score, badge, historyNote }: ScoreBlockProps) => {
  const scored = typeof score === 'number' && Number.isFinite(score);

  return (
    <div className={styles.block}>
      {scored ? (
        <>
          <div className={styles.scoreRow}>
            <div className={styles.score}>{Math.round(score)}</div>
            {historyNote}
            {badge}
          </div>
          <div className={styles.label}>Fit score</div>
        </>
      ) : (
        <>
          {/*
            Not a zero. Zero on a 0-100 scale reads as the worst possible
            candidate; the truth is that nobody scored them. The matching
            engine's own create_fallback_result uses null for exactly this,
            and overall_score() returns None rather than 0 for the same reason.
          */}
          <div className={styles.unscored}>Not scored yet</div>
          <div className={styles.note}>
            This candidate has not been through a Rio interview, so there is no
            fit score to show.
          </div>
        </>
      )}

      {scored ? (
        <span className={styles.attribution}>
          <span aria-hidden="true">◆</span> Avrio Intelligence
        </span>
      ) : null}
    </div>
  );
});

ScoreBlock.displayName = 'ScoreBlock';

export default ScoreBlock;
